"use client";
import React, { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "../styles/Blog.css";

/* Traveller images */
import blog1 from "../assets/tt6.png";
import blog2 from "../assets/tt4.jpg";
import blog3 from "../assets/tt27.jpeg";
import blog4 from "../assets/tt25.jpeg";
import blog5 from "../assets/tt18.png";

const BlogTimeline = ({ data }) => {
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const [timelineHeight, setTimelineHeight] = useState(0);

  /* AOS animation init */
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  /* Responsive timeline height */
  useEffect(() => {
    const updateHeight = () => {
      if (!timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      setTimelineHeight(rect.height);
    };

    updateHeight();

    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);

    const timer = setTimeout(updateHeight, 300);

    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
      clearTimeout(timer);
    };
  }, [data]);

  /* Scroll progress */
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 20%", "end 80%"],
  });

  const heightTransform = useTransform(
    scrollYProgress,
    [0, 1],
    [0, timelineHeight]
  );

  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="blog-timeline-container" ref={containerRef}>
      <div ref={timelineRef} className="blog-timeline">
        {data.map((item, index) => (
          <article key={index} className="blog-step">
            <div className="blog-step-left">
              <div className="blog-marker-outer">
                <div className="blog-marker-inner"></div>
              </div>

              <h3>{item.title}</h3>
            </div>

            <div className="blog-step-content">
              <img
                src={item.image}
                alt={`${item.title} | Maha Cabs Tempo Traveller in Mayiladuthurai`}
                data-aos="fade-left"
                loading="lazy"
              />

              <div data-aos="fade-left">
                <p className="blog-p1">{item.text}</p>

                {item.points &&
                  item.points.map((p, i) => (
                    <p key={i} className="blog-point">
                      <span className="gold-arrow">★</span> {p}
                    </p>
                  ))}
              </div>
            </div>
          </article>
        ))}

        {/* Progress line */}
        <div
          style={{ height: `${timelineHeight}px` }}
          className="blog-progress-wrapper"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="blog-progress-bar"
          />
        </div>
      </div>
    </div>
  );
};

const Blog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    
    document.title =
      "Mahacabs Blog | Best Tempo Traveller in Mayiladuthurai | Group Travel India";

    let metaDesc = document.querySelector("meta[name='description']");
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "Read Mahacabs blog on luxury 18-seater Tempo Traveller in Mayiladuthurai. Best for family trips, pilgrimages, weddings, corporate tours, and all-India travel with professional drivers."
    );

   
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://yourwebsite.com/blog");

 
const schemaScript = document.createElement("script");
schemaScript.type = "application/ld+json";
schemaScript.innerHTML = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Mahacabs",
      "url": "https://mahacabs.co",
      "logo": "https://mahacabs.co/logo4.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+919486589488",
        "contactType": "Customer Service"
      }
    },
    {
      "@type": "LocalBusiness",
      "name": "Mahacabs Tempo Traveller Service",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "State Bank Road, Near Vijaya Theater",
        "addressLocality": "Mayiladuthurai",
        "postalCode": "609001",
        "addressCountry": "IN"
      },
      "telephone": "+919486589488",
      "url": "https://mahacabs.co"
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://mahacabs.co"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Blog",
          "item": "https://mahacabs.co/blog"
        }
      ]
    },
    {
      "@type": "Article",
      "headline": "Mahacabs Tempo Traveller Blog",
      "author": { "@type": "Organization", "name": "Mahacabs" },
      "publisher": {
        "@type": "Organization",
        "name": "Mahacabs",
        "logo": { "@type": "ImageObject", "url": "https://mahacabs.co/logo4.png" }
      },
      "datePublished": "2026-02-01",
      "image": [
        blog1,
        blog2,
        blog3,
        blog4,
        blog5
      ]
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Where can I book a Tempo Traveller in Mayiladuthurai?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can book an 18-seater luxury Tempo Traveller from Mahacabs in Mayiladuthurai via call or WhatsApp."
          }
        },
        {
          "@type": "Question",
          "name": "Is Mahacabs Tempo Traveller good for long trips?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Mahacabs provides AC Tempo Travellers with pushback seats, entertainment, and experienced drivers for safe long-distance travel."
          }
        }
      ]
    }
  ]
});


    document.head.appendChild(schemaScript);

    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);


  const blogData = [
    {
      title: "Tempo Traveller in Mayiladuthurai",
      image: blog1,
      text:
        "Mahacabs offers safe, luxury, and comfortable 18-seater Tempo Traveller rentals in Mayiladuthurai for families, pilgrimages, weddings, corporate tours, and group travel across India with experienced professional drivers.",
      points: [
        "Luxury 18-seater AC Tempo Traveller",
        "Professional, licensed drivers",
        "Safe & secure group travel",
        "24/7 call & WhatsApp booking support",
      ],
    },
    {
      title: "Affordable All India Trips",
      image: blog2,
      text:
        "Travel anywhere in India at affordable pricing with Mahacabs AC Tempo Travellers designed for long-distance comfort, family safety, and hassle-free group journeys.",
      points: [
        "Budget-friendly tour packages",
        "Comfortable long-distance travel",
        "Corporate & family tour support",
        "Reliable nationwide cab service",
      ],
    },
    {
      title: "Luxury Comfort Features",
      image: blog3,
      text:
        "Our Tempo Travellers come with premium interiors, reclining seats, AC cooling, and entertainment systems ensuring a first-class travel experience for every passenger.",
      points: [
        "Pushback luxury seats",
        "AC & Non-AC options available",
        "LED TV & music system",
        "High-quality sound speakers",
        "Free WiFi onboard",
      ],
    },
    {
      title: "Experienced Drivers Safe & Secure",
      image: blog4,
      text:
        "Mahacabs driver are highly trained, well-behaved, and experienced in all-India routes, ensuring safe, smooth, and punctual journeys for every group trip.",
      points: [
        "Professional licensed driver",
        "Safe long-distance driving",
        "Well-maintained vehicle",
        "Customer-first service approach",
      ],
    },
    {
      title: "Book Your 18-Seater Traveller",
      image: blog5,
      text:
        "Planning a wedding, temple trip, or family tour? Book Mahacabs 18-seater Tempo Traveller in Mayiladuthurai today and enjoy stress-free, comfortable group travel.",
      points: [
        "Perfect for weddings & tours",
        "Clean and sanitized vehicles",
        "Instant booking confirmation",
        "Affordable group travel packages",
      ],
    },
  ];

  const handleMove = (e) => {
    const btn = e.currentTarget;
    const circle = btn.querySelector(".hover-circle");

    if (!circle) return;

    const rect = btn.getBoundingClientRect();
    circle.style.left = e.clientX - rect.left + "px";
    circle.style.top = e.clientY - rect.top + "px";
  };

  return (
    <>
      <section className="blog-section">
        <h1>Mahacabs Blogs</h1>
        <p>
          Travel comfortably across India with Mahacabs luxury Tempo Traveller.
          Enjoy affordable pricing, premium interiors, and professional drivers
          for every journey. Best 18-Seater Tempo Traveller in Mayiladuthurai
          for family trips, pilgrimages, weddings, and corporate tours.
        </p>
      </section>

      <BlogTimeline data={blogData} />

      {/* CTA Section */}
      <section className="blog-cta">
        <h2>Book Your Tempo Traveller Today</h2>
        <p>
          Travel safely with Mahacabs in Mayiladuthurai. Call or WhatsApp now for
          instant booking confirmation and best group travel packages.
        </p>

        <div className="blog-cta-buttons">
          <a
            href="tel:+919486589488"
            className="cta-call btn-6"
            onMouseMove={handleMove}
            title="Call Mahacabs for Tempo Traveller Booking"
          >
            <span className="hover-circle"></span>
            📞 Call Now
          </a>

          <a
            href="https://wa.me/919486589488"
            target="_blank"
            rel="noreferrer"
            className="cta-whatsapp btn-6"
            onMouseMove={handleMove}
            title="WhatsApp Mahacabs for Tempo Traveller Booking"
          >
            <span className="hover-circle"></span>
            WhatsApp Booking
          </a>
        </div>
      </section>
    </>
  );
};

export default Blog;
